import {
  Checkbox,
  Col,
  Flex,
  Form,
  FormItemProps,
  Input,
  Modal,
  Row,
  Select,
  Spin
} from "antd";
import { addOrUpdateContact, getContactById } from "api/services/ContactService";
import { getLocation } from "api/services/LocationService";
import {
  TContact,
  TContractGroupPicklist,
  TEditContact,
  TLocationPicklist,
  TRegionPicklist,
  TSpecialityPicklist
} from "api/services/models/types";
import { useTDAuthentication } from "auth/AuthenticationProvider";
import { AxiosError } from "axios";
import { CollapsibleGroup } from "components/CollapsibleGroup";
import { Header } from "components/common/Header";
import MultiSelectWithAll from "components/common/MultiSelectWithAll";
import ErrorViewer from "components/ErrorViewer";
import { usePicklists } from "components/picklists/PicklistContextProvider";
import { useEffect, useState } from "react";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
// @ts-ignore
import "react-phone-number-input/style.css";

type TAddOrUpdateProviderProps = {
  contact?: TContact;
  onClose: (contact?: TContact) => void;
};

enum ContactGroups {
  ADMINISTRATIVE_CONTACT = "A",
  BILLING_CONTACT = "B",
  GENERAL_CONTACT = "G",
  OFFICE_MANAGER = "O",
  PROVIDER = "P",
  TRANSCRIPTION = "R",
  SURVEY_RESPONDER = "S",
  TECHNICAL_CONTACT = "T"
}

type TFormRow = {
  key: string | number;
  gutter: number;
  columns: (FormItemProps & { key: string | number })[];
};

type TFormGroup = {
  key: string | number;
  title?: string; 
  rows: TFormRow[];
};

const makeFields = (
  regions: TRegionPicklist[],
  locations: TLocationPicklist[],
  specialities: TSpecialityPicklist[],
  contactGroups?: TContractGroupPicklist[],
  selectedContactGroups?: string[],
  selectedRegions?: string[],
  setSelectedLocations?: React.Dispatch<React.SetStateAction<string[]>>,
  localContact?: Partial<TEditContact>,
) => {
  const isBillingContact = selectedContactGroups?.includes(ContactGroups.BILLING_CONTACT);
  const isProvider = selectedContactGroups?.includes(ContactGroups.PROVIDER);

  const fields: TFormGroup[] = [
    {
      key: "basic-info",
      title: "Basic Info",
      rows: [
        {
          key: "row-1",
          gutter: 8,
          columns: [
            {
              key: 1,
              name: "title",
              label: "Title",
              hidden: false,
              children: <Input className="filter-input-transparent" />,
              rules: [{ max: 50 }],
            }
          ]
        },
        {
          key: "row-2",
          gutter: 8,
          columns: [
            {
              name: "firstName",
              label: "First Name",
              key: 1,
              children: <Input className="filter-input-transparent" />,
              rules: [{ required: true, max: 25, message: "Please enter First Name" }],
            },
            {
              name: "middleInitial",
              label: "Middle",
              key: 2,
              children: <Input maxLength={1} className="filter-input-transparent" />,
            },
            {
              name: "lastName",
              label: "Last Name",
              key: 3,
              children: <Input className="filter-input-transparent" />,
              rules: [{ required: true, max: 25, message: "Please enter Last Name" }],
            }
          ]
        },
        {
          key: "row-3",
          gutter: 8,
          columns: [
            {
              name: "locationId",
              label: "Primary Location",
              key: 1,
              children: (
                <Select
                  variant="borderless"
                  placeholder="Please select a location"
                  fieldNames={{ label: "locationName", value: "locationId" }}
                  options={locations.sort((a, b) => a.locationName.localeCompare(b.locationName))}
                  className="filter-input-transparent"
                />
              ),
              rules: [{ required: true, message: "Please select Location ID" }],
            },
            {
              name: "regions",
              label: "Region",
              key: 2,
              children: (
                <MultiSelectWithAll
                  variant="borderless"
                  fieldNames={{ label: "regionName", value: "regionId" }}
                  options={regions.sort((a, b) => a.regionName.localeCompare(b.regionName))}
                  allowClear
                  className="filter-input-transparent"
                />
              ),
              rules: [{ required: true, message: "Please select Region" }],
            },
          ]
        },
      ]
    },
    {
      key: "address-info",
      title: "Address Info",
      rows: [
        {
          key: "row-1",
          gutter: 8,
          columns: [
            {
              name: "address1",
              label: "Address 1",
              key: 1,
              children: <Input className="filter-input-transparent" />
            },
            {
              name: "address2",
              label: "Address 2",
              key: 2,
              children: <Input className="filter-input-transparent" />
            },
            {
              name: "address3",
              label: "Address 3",
              key: 3,
              children: <Input className="filter-input-transparent" />
            },
          ]
        },
        {
          key: "row-2",
          gutter: 8,
          columns: [
            {
              name: "city",
              label: "City",
              key: 1,
              children: <Input className="filter-input-transparent" />
            },
            {
              name: "state",
              label: "State",
              key: 2,
              children: (
                <Input
                  className="filter-input-transparent"
                  maxLength={2}
                  pattern="[A-Za-z]{2}"
                  title="Enter exactly 2 letters"
                />
              )
            },
            {
              name: "zip",
              label: "Zip",
              key: 3,
              children: (
                <Input type="number" style={{ width: "100%" }} className="filter-input-transparent" />
              )
            },
          ]
        },
      ]
    },
    {
      key: "contact-info",
      title: "Contact Info",
      rows: [
        {
          key: "row-1",
          gutter: 8,
          columns: [
            {
              name: "phone1",
              label: "Phone 1",
              key: 1,
              children: (
                <PhoneInput
                  defaultCountry="US"
                  className="PhoneInput"
                  onChange={() => {}}
                />
              ),
              rules: [
                { required: true, message: "Phone number is required!" },
                {
                  validator: (_, value) => {
                    if (!value || isValidPhoneNumber(value)) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error("Please enter a valid phone number!"));
                  },
                },
              ]
            },
            {
              name: "phone1Ext",
              label: "Ext",
              key: 2,
              children: <Input style={{ width: "100%" }} className="filter-input-transparent" maxLength={6} />,
              rules: [
                {
                  pattern: /^\d{1,6}$/,
                  message: "Extension must be 1 to 6 digits!",
                }
              ]
            },
          ]
        },
        {
          key: "row-2",
          gutter: 8,
          columns: [
            {
              name: "phone2",
              label: "Phone 2",
              key: 1,
              children: (
                <PhoneInput
                  defaultCountry="US"
                  className="PhoneInput"
                  onChange={() => {}}
                />
              ),
              rules: [
                {
                  validator: (_, value) => {
                    if (!value || isValidPhoneNumber(value)) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error("Please enter a valid phone number!"));
                  },
                },
              ]
            },
            {
              name: "phone2Ext",
              label: "Ext",
              key: 2,
              children: <Input style={{ width: "100%" }} className="filter-input-transparent" maxLength={6} />,
              rules: [
                {
                  pattern: /^\d{1,6}$/,
                  message: "Extension must be 1 to 6 digits!"
                }
              ]
            },
          ]
        },
        {
          key: "row-3",
          gutter: 8,
          columns: [
            {
              name: "phone3",
              label: "Mobile",
              key: 1,
              children: (
                <PhoneInput
                  defaultCountry="US"
                  className="PhoneInput"
                  onChange={() => {}}
                />
              ),
              rules: [
                {
                  validator: (_, value) => {
                    if (!value || isValidPhoneNumber(value)) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error("Please enter a valid phone number!"));
                  },
                },
              ]
            },
          ]
        },
        {
          key: "row-4",
          gutter: 8,
          columns: [
            {
              name: "fax",
              label: "Fax",
              key: 1,
              children: <Input style={{ width: "100%" }} className="filter-input-transparent" />,
              rules: [
                {
                  pattern: /^\d{10}$/,
                  message: "Fax is not valid!"
                }
              ]
            },
          ]
        },
        {
          key: "row-5",
          gutter: 8,
          columns: [
            {
              name: "email",
              label: "Email",
              key: 1,
              rules: [{ type: "email" }],
              children: <Input className="filter-input-transparent" />
            }
          ]
        },
        {
          key: "row-6",
          gutter: 8,
          columns: [
            {
              name: "web",
              label: "Web",
              key: 1,
              rules: [{ type: "url" }],
              children: <Input className="filter-input-transparent" />
            }
          ]
        }
      ]
    },
    {
      key: "contact-group-info",
      title: "Contact Group Info",
      rows: [
        {
          key: "row-1",
          gutter: 8,
          columns: [
            {
              name: "contactGroups",
              label: "Contact Groups",
              key: 1,
              rules: [{ required: true, message: "Please select Contact Groups" }],
              children: (
                <Select
                  mode="multiple"
                  fieldNames={{ label: "name", value: "id" }}
                  maxTagCount={"responsive"}
                  options={contactGroups?.sort((a, b) => a.name.localeCompare(b.name))}
                  variant="borderless"
                  className="filter-input-transparent"
                />
              )
            },
          ]
        },
        {
          key: "row-2",
          gutter: 8,
          columns: [
            {
              name: "billingAccountName",
              label: "Billing Account Name",
              key: 1,
              hidden: !isBillingContact,
              children: <Input className="filter-input-transparent" />,
              rules: [
                {
                  required: isBillingContact,
                  max: 255,
                  message: "Please enter Billing Account Name"
                }
              ]
            },
          ]
        },
        {
          key: "row-3",
          gutter: 8,
          columns: [
            {
              name: ["provider", "credentials"],
              label: "Credentials",
              key: 1,
              hidden: !isProvider,
              children: <Input className="filter-input-transparent" />,
              rules: [{ max: 60 }]
            },
            {
              name: ["provider", "designation"],
              label: "Designation",
              key: 2,
              hidden: !isProvider,
              children: <Input className="filter-input-transparent" />,
              rules: [{ max: 30 }]
            },
          ]
        },
        {
          key: "row-4",
          gutter: 8,
          columns: [
            {
              name: ["provider", "displayName"],
              label: "Display Name",
              key: 1,
              children: <Input className="filter-input-transparent" />,
              hidden: !isProvider,
              rules: [
                { required: isProvider, max: 100, message: "Please enter Provider Display Name" }
              ]
            },
            {
              name: ["provider", "specialityId"],
              label: "Speciality",
              key: 2,
              hidden: !isProvider,
              children: (
                <Select
                  variant="borderless"
                  fieldNames={{ label: "description", value: "specialityId" }}
                  options={specialities}
                  className="filter-input-transparent"
                />
              ),
              rules: [{ required: isProvider, message: "Please select Provider Specialty ID" }]
            },
            {
              name: ["provider", "providerLocations"],
              label: "Visit Location",
              key: 3,
              hidden: !isProvider,
              children: (
                <MultiSelectWithAll
                  variant="borderless"
                  fieldNames={{ label: "locationName", value: "locationId" }}
                  options={locations.filter(l => selectedRegions?.includes(l.regionId))}
                  allowClear
                  showSearch={false}
                  maxTagCount={"responsive"}
                  className="filter-input-transparent"
                  onChange={values => {
                    setSelectedLocations?.(Array.isArray(values) ? values.map(String) : []);
                  }}
                />
              ),
              rules: [{ required: isProvider, message: "Please select Provider Locations" }]
            }
          ]
        },
        {
          key: "row-5",
          gutter: 8,
          columns: [
            {
              name: ["provider", "signature"],
              label: "Signature",
              key: 1,
              hidden: !isProvider,
              children: <Input className="filter-input-transparent" />
            },
            {
              name: ["provider", "isEsignEnabled"],
              label: "Enable eSignature",
              key: 2,
              layout: "horizontal",
              hidden: !isProvider,
              valuePropName: "checked",
              style: {
                marginTop: "32px"
              },
              children: <Checkbox />
            },
          ]
        },
        {
          key: "row-6",
          gutter: 8,
          columns: [
            {
              name: ["provider", "dssId"],
              label: "DSS ID",
              key: 1,
              hidden: !isProvider,
              children: <Input className="filter-input-transparent" />,
              rules: [{ max: 20 }],
              initialValue: "XXXX"
            },
            {
              name: ["provider", "phoneId"],
              label: "Phone Id",
              key: 2,
              hidden: !isProvider,
              children: <Input readOnly={localContact?.provider?.isVTUser} className="filter-input-transparent" />,
              rules: [{ max: 20 }]
            },
            {
              name: ["provider", "extProviderId"],
              label: "Ext. Provider Id",
              key: 3,
              hidden: !isProvider,
              children: <Input className="filter-input-transparent" />,
              rules: [{ max: 25 }]
            },
          ]
        }
      ]
    }
  ]

  return fields;
};

const AddOrUpdateProvider = ({ contact, onClose }: TAddOrUpdateProviderProps) => {
  const { accountId } = useTDAuthentication();
  const [localContact, setLocalContact] = useState<Partial<TEditContact>>();
  const [form] = Form.useForm();
  const { locations, regions, specialities, contactGroups } = usePicklists();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<AxiosError | null>();
  const [selectedContactGroups, setSelectedContactGroups] = useState<string[] | undefined>(
    localContact?.contactGroups
  );
  const [selectedRegions, setSelectedRegions] = useState<string[] | undefined>(
    localContact?.regions
  );
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [prevSelectedRegions, setPrevSelectedRegions] = useState<string[] | undefined>();
  useEffect(() => {
    if (accountId && contact != null) {
      setLoading(true);
      setError(null);
      getContactById(accountId, contact.contactId)
        .then(setLocalContact)
        .catch(setError)
        .finally(() => setLoading(false));
    }
  }, [contact]);

  useEffect(() => {
    if (prevSelectedRegions !== undefined && prevSelectedRegions !== selectedRegions) {
      setSelectedLocations([]);
      form.setFieldsValue({ provider: { providerLocations: [] } });
    }
    setPrevSelectedRegions(selectedRegions);
  }, [selectedRegions]);

  useEffect(() => {
    if (localContact) {
      setSelectedContactGroups(localContact.contactGroups);
      setSelectedRegions(localContact.regions);
      form.setFieldsValue(localContact);
    }
  }, [localContact]);

  const onFormValuesChange = (values: Partial<TEditContact>) => {
    if ("contactGroups" in values && values["contactGroups"]) {
      setSelectedContactGroups(values["contactGroups"]);
    }
    if ("locationId" in values) {
      onChangeLocation(values["locationId"] as number);
    }
    if ("regions" in values) {
      // setSelectedRegions(values["regions"]);
      setSelectedRegions(Array.isArray(values["regions"]) ? values["regions"] : []);
    }
  };

  const onChangeLocation = (locationId: number) => {
    getLocation(accountId!, locationId)
      .then(location => {
        form.setFieldsValue({
          address1: location.address1,
          address2: location.address2,
          city: location.city,
          state: location.state,
          zip: location.zip,
          phone1: location.phone,
          fax: location.fax
        });
      })
      .catch(console.error);
  };

  const onSave = () => {
    form
      .validateFields()
      .then(() => form.submit())
      .catch(setError);
  };

  const onFinish = async (values: TEditContact) => {
    const isProvider = values.contactGroups?.some(g => g === ContactGroups.PROVIDER);

    const updatedContact: TEditContact = {
      accountId: accountId!,
      ...localContact,
      ...values,
      zip: values.zip?.toString(),
      phone1: values.phone1?.toString(),
      phone2: values.phone2?.toString(),
      phone3: values.phone3?.toString(),
      isProvider,
      provider: !isProvider
        ? undefined
        : {
            accountId: accountId!,
            ...localContact?.provider,
            ...values.provider,
            firstName: values.firstName,
            lastName: values.lastName,
            middleInitial: values.middleInitial,
            locationId: values.locationId
          }
    };
    setLoading(true);
    addOrUpdateContact(updatedContact)
      .then(onClose)
      .catch(setError)
      .finally(() => setLoading(false));
  };

  return (
    <Modal
      maskClosable={false}
      title={
        <Header
          isEditing={contact != null}
          header="Add Contact/Provider"
          editHeader="Edit Contact/Provider"
          onSubmit={onSave}
          onClose={onClose}
        />
      }
      width={700}
      open={true}
      footer={null}
      closeIcon={null}
      onCancel={() => onClose()}
      destroyOnClose={true}
      centered={true}>
      {loading && (
        <Flex justify="space-around" style={{ width: "100%", textAlign: "center" }}>
          <Spin tip="Saving, please wait..." />
        </Flex>
      )}
      {error && <ErrorViewer error={error} />}
      {!loading && (
        <Form
          style={{ maxHeight: "750px", overflowY: "auto" }}
          form={form}
          size="small"
          name="add_or_update_contact"
          onFinish={onFinish}
          layout="vertical"
          validateTrigger={["onChange", "onBlur"]}
          clearOnDestroy={true}
          autoComplete="off"
          onValuesChange={onFormValuesChange}
          initialValues={{
            ...localContact,
            provider: {
              ...localContact?.provider,
              dssId: localContact?.provider?.dssId || "XXXX" // Ensure default value is applied - Bhargav
            }
          }}>
          {makeFields(
            regions ?? [],
            locations ?? [],
            specialities ?? [],
            contactGroups ?? [],
            selectedContactGroups,
            selectedRegions,
            setSelectedLocations
            , localContact
          ).map(group => (
            <CollapsibleGroup key={group.key} title={group.title}>
              {group.rows.map(row => (
                <Row gutter={row.gutter} key={row.key}>
                  {row.columns.map(col => (
                    <Col key={col.key} xs={24} sm={12} md={8} lg={8}>
                      <Form.Item
                        key={col.name}
                        name={col.name}
                        label={
                          <label htmlFor={col.name} className="input-label" style={{ marginBottom: col.label === "Enable eSignature" ? "0px" : "" }}>
                            {col.label}
                          </label>
                        }
                        valuePropName={col.valuePropName}
                        rules={col.rules}
                        layout={col.layout}
                        style={col.style}
                        hidden={col.hidden}>
                        {col.children}
                      </Form.Item>
                    </Col>
                  ))}
                </Row>
              ))}
            </CollapsibleGroup>
          ))}
        </Form>
      )}
    </Modal>
  );
};

export default AddOrUpdateProvider;
